<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import AddProduct from './AddProduct.vue';
import EditProduct from './EditProduct.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const selectedProduct = ref({});
const deleteDialogRef = ref(null);

const deleteMessage = ref('Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.');

const records = computed(() => store.getters['products/getProducts'] || []);
const uiFlags = computed(() => store.getters['products/getUIFlags'] || { isFetching: false });

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedProduct.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  selectedProduct.value = response;
  deleteMessage.value = `Tem certeza que deseja excluir o produto "${response.name}"? Esta ação não pode ser desfeita.`;
  deleteDialogRef.value?.open();
};
const closeDeletePopup = () => {
  deleteDialogRef.value?.close();
};

const deleteProduct = async id => {
  try {
    await store.dispatch('products/delete', id);
    useAlert('Produto excluído com sucesso!');
  } catch (error) {
    // Extrair mensagem de erro do response se disponível
    let errorMessage = 'Erro ao excluir produto';
    
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.response?.data?.errors?.base?.[0]) {
      errorMessage = error.response.data.errors.base[0];
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    useAlert(errorMessage);
  } finally {
    loading.value[selectedProduct.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedProduct.value.id] = true;
  closeDeletePopup();
  deleteProduct(selectedProduct.value.id);
};

const formatCurrency = (value, currency = 'BRL') => {
  const symbols = { BRL: 'R$', USD: '$', EUR: '€' };
  return `${symbols[currency] || currency} ${Number(value).toFixed(2)}`;
};

const tableHeaders = computed(() => {
  return [
    t('PRODUCT_MGMT.LIST.TABLE_HEADER.NAME'),
    t('PRODUCT_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
    t('PRODUCT_MGMT.LIST.TABLE_HEADER.VALUE'),
    t('PRODUCT_MGMT.LIST.TABLE_HEADER.TYPE'),
  ];
});

onBeforeMount(() => {
  store.dispatch('products/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('PRODUCT_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('PRODUCT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PRODUCT_MGMT.HEADER')"
        :description="$t('PRODUCT_MGMT.DESCRIPTION')"
        :link-text="$t('PRODUCT_MGMT.LEARN_MORE')"
        feature-name="products"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('PRODUCT_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <thead>
          <tr class="border-b border-n-weak">
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 pr-4 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider first:pl-0"
            >
              {{ thHeader }}
            </th>
            <th class="relative py-4 pl-3 pr-0">
              <span class="sr-only">{{ $t('GENERAL.ACTIONS') }}</span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <tr v-for="product in records" :key="product.id">
            <td class="py-4 pr-4 first:pl-0">
              <div class="flex items-center">
                <div
                  class="h-8 w-8 rounded-lg mr-3 flex items-center justify-center text-white font-medium text-sm"
                  :style="{ backgroundColor: product.color || '#1f2937' }"
                >
                  <i class="i-lucide-package size-4" />
                </div>
                <div>
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ product.name }}
                  </div>
                </div>
              </div>
            </td>
            <td class="py-4 pr-4">
              <div class="text-sm text-n-slate-11 max-w-xs truncate">
                {{ product.description }}
              </div>
            </td>
            <td class="py-4 pr-4">
              <div class="text-sm font-medium text-n-slate-12">
                {{ product.formatted_price || formatCurrency(product.base_price || product.value, product.currency) }}
              </div>
            </td>
            <td class="py-4 pr-4">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="
                  product.type === 'product'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-green-100 text-green-800'
                "
              >
                {{ product.type === 'product' ? 'Produto' : 'Serviço' }}
              </span>
            </td>
            <td class="py-4 pl-3 pr-0 text-right text-sm font-medium">
              <div class="flex items-center justify-end gap-1">
                <Button
                  v-tooltip.top="$t('PRODUCT_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-lucide-edit"
                  slate
                  xs
                  @click="openEditPopup(product)"
                />
                <Button
                  v-tooltip.top="$t('PRODUCT_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  ruby
                  xs
                  :loading="loading[product.id]"
                  @click="openDeletePopup(product)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <!-- Add Product Modal -->
  <AddProduct
    v-if="showAddPopup"
    @close="hideAddPopup"
  />

  <!-- Edit Product Modal -->
  <EditProduct
    v-if="showEditPopup"
    :product="selectedProduct"
    @close="hideEditPopup"
  />

  <!-- Delete Product Modal -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    title="Confirmar Exclusão"
    :description="deleteMessage"
    confirmButtonLabel="Sim, Excluir"
    cancelButtonLabel="Cancelar"
    @confirm="confirmDeletion"
    @close="closeDeletePopup"
  />
</template>
